import { useContext, useState, useMemo, useCallback } from 'react';
import { PersonDataContext } from '../../contexts/person-data-context';
import { ItemRender } from './item-renderer';
import shield from '../../images/grey-shield.svg';
import { NoDocumentsContainer } from '../base/simple-styled-components';
import ClockGraphic from '../../images/clock.svg';
import useMobileView from '../../hooks/use-mobile-view';
import { MobileItemRenderer } from './mobile-item-renderer';
import ErrorBoundary from '../../utils/error-boundary';
import { useQuery } from '@tanstack/react-query';
import { getAvailableTime } from './api';
import { unavailableTime } from './util';
import { Spinner } from '../base/spinner';
import BaseDatePicker from '../base/date-picker';
import ClaimsDropDownList from '../base/claims-drop-down-list';
import * as dm from '../../data-models';
import dayjs from 'dayjs';

type Props = {
  showMobile?: boolean;
};

const FallBack: React.FC = () => {
  return (
    <NoDocumentsContainer>
      <img src={ClockGraphic} alt="" width="179" height="148" />
      <div className="tw-mt-4 tw-italic" id="time-empty">
        We are unable to provide information regarding the amount of leave used
        or available. Please review your most recent notice(s) for details on
        your leave(s).
      </div>
    </NoDocumentsContainer>
  );
};

const DEFAULT_OPTION = 'View All Leaves';

const AvailableTime: React.FC<Props> = ({ showMobile = false }) => {
  const isMobileView = useMobileView(1050, showMobile);
  const { personData } = useContext(PersonDataContext);
  const [selectedClaim, setSelectedClaim] = useState<dm.Leave | null>(null);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [pickerDate, setPickerDate] = useState<Date | null>(null);

  const {
    data: entitlementPlans,
    error,
    isLoading,
  } = useQuery(['leaveAvailibility', personData, selectedDate], () =>
    getAvailableTime(personData, selectedDate),
  );

  const leaves =
    personData?.cases?.leaves?.filter(
      (leave) => (leave.selectedLeavePlans?.length ?? 0) > 0,
    ) || [];

  const filteredEntitlementPlans = useMemo(() => {
    if (!entitlementPlans) {
      return entitlementPlans;
    }

    const availablePlans = entitlementPlans.filter(
      (plan) => !unavailableTime(plan.mainEntitlement),
    );

    if (!selectedClaim) {
      return availablePlans;
    }

    const selectedLeavePlanIds =
      selectedClaim.selectedLeavePlans?.map((plan) => plan.leavePlanId) || [];

    return availablePlans.filter((plan) => {
      // Check if the main entitlement ID matches
      if (selectedLeavePlanIds.includes(plan.mainEntitlement.id)) {
        return true;
      }

      // Check if any child entitlement IDs match (for shared entitlement plans)
      if (plan.entitlements && plan.entitlements.length > 0) {
        return plan.entitlements.some((entitlement) =>
          selectedLeavePlanIds.includes(entitlement.id),
        );
      }

      return false;
    });
  }, [entitlementPlans, selectedClaim]);

  const onDateChange = useCallback((date: Date | null) => {
    setPickerDate(date);
    const formattedDate = date ? dayjs(date).format('YYYY-MM-DD') : null;
    setSelectedDate(formattedDate);
  }, []);

  const onClaimsChange = useCallback((e: any) => {
    const value = e?.target?.value;
    if (value && typeof value === 'object' && value.caseId !== DEFAULT_OPTION) {
      setSelectedClaim(value);
    } else {
      setSelectedClaim(null);
    }
  }, []);

  if (error) {
    return <FallBack />;
  }

  if (isLoading) {
    return <Spinner />;
  }

  return (
    <>
      <div className="tw-text-4xl tw-mb-2 available-time-header tw-text-unm-interactive01-800 tw-font-normal tw-font-ubuntu">
        Available Time
      </div>
      <div id="available-time" className="tw-max-w-full">
        {entitlementPlans && entitlementPlans.length > 0 ? (
          <ErrorBoundary fallback={<FallBack />}>
            <>
              <div className="tw-flex tw-flex-col md:tw-flex-row tw-gap-4 tw-mb-6">
                <div className="tw-w-full md:tw-w-1/3">
                  <BaseDatePicker
                    placeholder="Balance As Of"
                    onChange={onDateChange}
                    value={pickerDate}
                    min={dayjs().subtract(2, 'year').toDate()}
                    max={dayjs().add(1, 'year').toDate()}
                  />
                </div>
                <div className="tw-w-full md:tw-w-2/3">
                  <div className="tw-w-full">
                    <ClaimsDropDownList
                      caseList={leaves}
                      defaultOption={DEFAULT_OPTION}
                      onChange={onClaimsChange}
                      viewAll
                      processedProps
                      autoHeight={true}
                    />
                  </div>
                </div>
              </div>
              {filteredEntitlementPlans?.map((plan) => {
                return isMobileView ? (
                  <MobileItemRenderer
                    data={plan}
                    key={plan.mainEntitlement.id}
                  />
                ) : (
                  <ItemRender data={plan} key={plan.mainEntitlement.id} />
                );
              })}
              <div className="tw-flex tw-justify-center tw-my-5 tw-p-5 tw-bg-[#EAEAEA4D] tw-w-[100%] border border-[#CFCFCF] tw-rounded-md">
                {!isMobileView && (
                  <div id="shield-icon">
                    <img
                      src={shield}
                      alt="shield"
                      className="tw-mt-[14px] tw-pr-4"
                    />
                  </div>
                )}
                The leave amounts displayed are approximate. They may be
                impacted by other leave requests and may not include time
                reported in the last two business days. Please review your most
                recent notice(s) for details on your leave(s). Although time may
                be available, you must be eligible and on leave for a qualifying
                reason to use such time.
              </div>
            </>
          </ErrorBoundary>
        ) : (
          <FallBack />
        )}
      </div>
    </>
  );
};

export default AvailableTime;
